import React from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import Iconify from "src/components/iconify";
import { useNavigate } from "react-router-dom";

import { setDialog } from "../../../features/dialog/dialogSlice";
import NoSitesYet from "../../@dashboard/sites/NoSitesYet";
import useAuth from "../../../hooks/useAuth";
import usePlanGate from "../../../hooks/usePlanGate";
import { FEATURE_KEYS } from "../../../utils/planFeatures";
import FeatureLockedDialog from "../../../components/dialog/FeatureLockedDialog";

// Match SettingsPage centered nav styling: spacing, border, and soft shadow.
const centeredNavItemSx = {
  maxWidth: "100%",
  mb: 1.5,
  borderRadius: 2,
  px: 2.5,
  py: 1.75,
  boxShadow: "0 12px 24px -18px rgba(0, 0, 0, 0.4)",
  border: "1px solid rgba(0, 0, 0, 0.12)",
};

const MemberMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isUser, isEditor, isAdmin, isSuperAdmin } = useAuth();
  const isNormalUser = isUser && !isEditor && !isAdmin && !isSuperAdmin;
  const {
    isAllowed: isAttendanceAllowed,
    isDialogOpen: isAttendanceDialogOpen,
    requiredPlanLabel: attendancePlanLabel,
    openDialog: openAttendanceDialog,
    closeDialog: closeAttendanceDialog,
    goToUpgrade: goToAttendanceUpgrade,
  } = usePlanGate(FEATURE_KEYS.TRAINING_ATTENDANCE);

  const {
    isAllowed: isPublicRegAllowed,
    isDialogOpen: isPublicRegDialogOpen,
    requiredPlanLabel: publicRegPlanLabel,
    openDialog: openPublicRegDialog,
    closeDialog: closePublicRegDialog,
    goToUpgrade: goToPublicRegUpgrade,
  } = usePlanGate(FEATURE_KEYS.PUBLIC_REGISTRATION);

  const handleOpenMemberForm = () => {
    dispatch(setDialog({ title: "Neues Mitglied erstellen", type: "CREATE" }));
  };

  const handleOpenImportMembers = () => {
    dispatch(
      setDialog({
        title: "Daten importieren",
        type: "DATA_IMPORT_WIZARD",
        content: "data-import",
      }),
    );
  };

  const handleDeleteAllMembers = () => {
    dispatch(setDialog({ title: "Mitglieder löschen", type: "DELETE" }));
  };

  const handleOpenRegistrationLinksDialog = () => {
    if (!isPublicRegAllowed) {
      openPublicRegDialog();
      return;
    }
    dispatch(
      setDialog({
        title: "Registrierungslink senden",
        type: "SEND_REGISTRATION_LINKS",
      }),
    );
  };

  const memberMenuItems = [
    {
      label: "Mitgliederübersicht",
      icon: "mdi:view-dashboard",
      onClick: () => navigate("overview"),
    },
    {
      label: "Neues Mitglied",
      icon: "fa-solid:user-plus",
      onClick: handleOpenMemberForm,
      staffOnly: true,
    },
    {
      label: "Anwesenheit",
      icon: "mdi:clipboard-check-outline",
      onClick: () =>
        isAttendanceAllowed ? navigate("attendance") : openAttendanceDialog(),
    },
    {
      label: "Selbstanmeldungen",
      icon: "mdi:account-clock",
      onClick: () =>
        isPublicRegAllowed
          ? navigate("self-registrations")
          : openPublicRegDialog(),
      staffOnly: true,
    },
    {
      label: "Registrierungslink senden",
      icon: "mdi:email-fast-outline",
      onClick: handleOpenRegistrationLinksDialog,
      staffOnly: true,
      adminOnly: true,
    },
    {
      label: "Daten importieren",
      icon: "mdi:file-upload",
      onClick: handleOpenImportMembers,
      staffOnly: true,
    },
    {
      label: "ALLE Mitglieder löschen",
      icon: "mdi:trash-can",
      onClick: handleDeleteAllMembers,
      tone: "danger",
      staffOnly: true,
    },
  ];

  const visibleMenuItems = memberMenuItems.filter((item) => {
    if (isNormalUser && item.staffOnly) return false;
    if (item.adminOnly && !(isAdmin || isSuperAdmin)) return false;
    return true;
  });

  return (
    <div>
      <Typography variant="h4" sx={{ mb: 0 }}>
        Mitglieder
      </Typography>

      <Box
        sx={{
          mt: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <List
          component="nav"
          aria-label="members navigation"
          sx={{ width: "100%", maxWidth: 560 }}
        >
          {visibleMenuItems.map((item) => (
            <ListItemButton
              key={item.label}
              onClick={item.onClick}
              sx={{
                ...centeredNavItemSx,
                ...(item.tone === "danger"
                  ? {
                      borderColor: "rgba(211, 47, 47, 0.4)",
                      color: "error.main",
                    }
                  : null),
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Iconify icon={item.icon} />
              </ListItemIcon>
              <ListItemText disableTypography primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* Keep your helper below the menu like before */}
      <NoSitesYet />
      <FeatureLockedDialog
        open={isAttendanceDialogOpen}
        onClose={closeAttendanceDialog}
        onUpgrade={goToAttendanceUpgrade}
        upgradePlanLabel={attendancePlanLabel}
      />
      <FeatureLockedDialog
        open={isPublicRegDialogOpen}
        onClose={closePublicRegDialog}
        onUpgrade={goToPublicRegUpgrade}
        upgradePlanLabel={publicRegPlanLabel}
      />
    </div>
  );
};

export default MemberMenu;
